import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { logQueueService } from '@/services/logQueue';
import { gpsService } from '@/services/gps';
import { mediaQueueService } from '@/services/mediaQueue';
import { assignmentRepo } from '@/data/repositories/assignmentRepo';
import { groupStops, type Stop } from '@/lib/grouping';
import { stopLogLocation } from '@/lib/logLocation';
import {
  DevSwitch,
  buildLog,
  MediaFields,
  AvsPickupFields,
  CaptureFields,
} from '@/types/domain';
import { TemperatureConfig } from '@/lib/config';

export type PickupPhase =
  | 'idle'
  | 'enRoute'
  | 'arrived'
  | 'temperatureCheck'
  | 'workBills'
  | 'signatureGate'
  | 'complete';

export interface Avs {
  inside: boolean;
  tailgate: boolean;
  residential: boolean;
  // DG is grouped with the other accessorials (Martin #30) and is pickup-only.
  dangerousGoods: boolean;
}

export const MAX_BILL_PHOTOS = 5;

interface BillState {
  noFreight: boolean;
  confirmed: boolean;
  notes: string;
  photos: Blob[];
  bolDoc: Blob | null;
}

interface PickupState {
  stop: Stop | null;
  phase: PickupPhase;
  requiresProbe: boolean;
  enteredTemperature: number | null;
  roomLeft: number | null;
  signerName: string;
  avs: Avs;
  bills: Record<string, BillState>;
  pendingSignature: Blob | null;
  error: string | null;

  loadStop: (idOrKey: string) => Promise<void>;
  startEnRoute: () => Promise<void>;
  arrive: () => Promise<void>;
  enterTemperature: (value: number) => void;
  confirmTemperature: (override?: boolean) => Promise<void>;
  refuseTemperature: () => Promise<void>;
  beginLoading: () => Promise<void>;
  cancelEnRoute: () => Promise<void>;
  stepBack: () => Promise<void>;
  cancelLoading: () => void;
  setRoomLeft: (n: number | null) => void;
  setSignerName: (name: string) => void;
  setAvs: (patch: Partial<Avs>) => void;
  setBillNoFreight: (id: string, value: boolean) => void;
  setBillConfirmed: (id: string, value: boolean) => void;
  setBillNotes: (id: string, notes: string) => void;
  addBillPhoto: (id: string, blob: Blob) => void;
  removeBillPhoto: (id: string, index: number) => void;
  setBillBolDoc: (id: string, blob: Blob | null) => void;
  captureSignature: (blob: Blob) => void;
  complete: () => Promise<void>;
  reset: () => void;
}

const temperatureExceeds = (t: number | null) =>
  t !== null && t > TemperatureConfig.thresholdCelsius;

const emptyAvs: Avs = { inside: false, tailgate: false, residential: false, dangerousGoods: false };
// Native writes uppercase YES/NO to FileMaker.
const yn = (b: boolean) => (b ? 'YES' : 'NO');

async function findStop(idOrKey: string): Promise<Stop | undefined> {
  const items = await assignmentRepo.getLoadItems();
  const stops = [...groupStops(items, 'pickup', false), ...groupStops(items, 'pickup', true)];
  return stops.find((s) => s.key === idOrKey || s.bills.some((b) => b.remF04Id === idOrKey));
}

const freshBills = (stop: Stop): Record<string, BillState> =>
  Object.fromEntries(
    stop.bills.map((b) => [
      b.remF04Id,
      { noFreight: false, confirmed: false, notes: '', photos: [], bolDoc: null },
    ]),
  );

export const usePickupStore = create<PickupState>()(
  persist(
    (set, get) => ({
  stop: null,
  phase: 'idle',
  requiresProbe: false,
  enteredTemperature: null,
  roomLeft: null,
  signerName: '',
  avs: { ...emptyAvs },
  bills: {},
  pendingSignature: null,
  error: null,

  loadStop: async (idOrKey) => {
    const stop = await findStop(idOrKey);
    if (!stop) {
      set({ error: 'Stop not found', stop: null });
      return;
    }
    const requiresProbe = stop.bills.some(
      (b) => !!b.remProbRequirement && b.remProbRequirement.trim().length > 0,
    );
    set({
      stop,
      phase: 'idle',
      requiresProbe,
      enteredTemperature: null,
      roomLeft: null,
      signerName: '',
      avs: { ...emptyAvs },
      bills: freshBills(stop),
      pendingSignature: null,
      error: null,
    });
  },

  startEnRoute: async () => {
    const stop = get().stop;
    if (!stop) return;
    set({ phase: 'enRoute' });
    // Native does NOT change the local switch on en-route -- the bill stays in
    // the load list (switch 1) until pickup completes (switch -> 2).
    const loc = stopLogLocation(stop);
    // Native attaches a one-shot GPS fix to the EN ROUTE log (best-effort).
    const fix = gpsService.peekFix();
    for (const b of stop.bills) {
      await logQueueService.enqueue({
        logType: buildLog.enRouteToPickup(loc),
        display: 'Display',
        assignmentId: b.remF04Id,
        tag: b.remF02Tag ?? undefined,
        latitude: fix?.latitude,
        longitude: fix?.longitude,
        accuracy: fix?.accuracy,
      });
    }
    // Route-level tracking is self-selecting (switch 2/5); a pickup en-route
    // bill is still switch 1, so no `gps break` fires until pickup completes --
    // matching native. Start it now so the delivery leg is covered seamlessly.
    gpsService.startRouteTracking();
  },

  arrive: async () => {
    const stop = get().stop;
    if (!stop) return;
    const loc = stopLogLocation(stop);
    for (const b of stop.bills) {
      await gpsService.captureOnce({
        assignmentId: b.remF04Id,
        display: 'Display',
        logType: buildLog.arrivedAtPickup(loc),
        tag: b.remF02Tag ?? undefined,
      });
    }
    set({ phase: 'arrived' });
  },

  cancelEnRoute: async () => {
    const stop = get().stop;
    if (!stop) return;
    await gpsService.stopRouteTracking();
    const loc = stopLogLocation(stop);
    const fix = gpsService.peekFix();
    for (const b of stop.bills) {
      await logQueueService.enqueue({
        logType: buildLog.enRoutePickupCanceled(loc),
        display: 'Display',
        assignmentId: b.remF04Id,
        tag: b.remF02Tag ?? undefined,
        latitude: fix?.latitude,
        longitude: fix?.longitude,
        accuracy: fix?.accuracy,
      });
    }
    await logQueueService.flush();
    set({ phase: 'idle' });
  },

  enterTemperature: (value) => set({ enteredTemperature: value }),

  confirmTemperature: async (_override = false) => {
    const { stop, enteredTemperature } = get();
    if (!stop || enteredTemperature === null) return;
    const loc = stopLogLocation(stop);
    for (const b of stop.bills) {
      await logQueueService.enqueue({
        logType: buildLog.temperatureProbe(loc),
        display: 'Display',
        assignmentId: b.remF04Id,
        tag: b.remF02Tag ?? undefined,
        additionalFields: { dev_f62_pup_Temperature_UPD: enteredTemperature },
      });
    }
    // Match native: probe is followed by the LOADING AT log, then bill work.
    for (const b of stop.bills) {
      await logQueueService.enqueue({
        logType: buildLog.loadingAt(loc),
        display: 'Display',
        assignmentId: b.remF04Id,
        tag: b.remF02Tag ?? undefined,
      });
    }
    set({ phase: 'workBills' });
  },

  refuseTemperature: async () => {
    const { stop, enteredTemperature } = get();
    if (!stop) return;
    const loc = stopLogLocation(stop);
    for (const b of stop.bills) {
      await logQueueService.enqueue({
        logType: buildLog.temperatureProbe(loc),
        display: 'Display',
        assignmentId: b.remF04Id,
        tag: b.remF02Tag ?? undefined,
        additionalFields: { dev_f62_pup_Temperature_UPD: enteredTemperature ?? null },
      });
      await logQueueService.enqueue({
        logType: buildLog.deadCallTemperature(),
        display: 'Display',
        assignmentId: b.remF04Id,
        tag: b.remF02Tag ?? undefined,
        additionalFields: { [CaptureFields.switch999]: '999' },
      });
      await assignmentRepo.markComplete(b.remF04Id);
    }
    set({ phase: 'complete' });
  },

  beginLoading: async () => {
    const { stop, requiresProbe } = get();
    if (!stop) return;
    if (requiresProbe) {
      set({ phase: 'temperatureCheck' });
      return;
    }
    const loc = stopLogLocation(stop);
    for (const b of stop.bills) {
      await logQueueService.enqueue({
        logType: buildLog.loadingAt(loc),
        display: 'Display',
        assignmentId: b.remF04Id,
        tag: b.remF02Tag ?? undefined,
      });
    }
    // Native shows no separate "loading" screen -- it goes straight to bill
    // work after LOADING AT. Room left is collected on the final step.
    set({ phase: 'workBills' });
  },

  cancelLoading: () => {
    const stop = get().stop;
    if (!stop) return;
    // Return to arrived and clear all bill capture (parity with native).
    set({ phase: 'arrived', bills: freshBills(stop) });
  },

  stepBack: async () => {
    const phase = get().phase;
    switch (phase) {
      case 'enRoute':
        await get().cancelEnRoute();
        break;
      case 'workBills':
        get().cancelLoading();
        break;
      case 'signatureGate':
        set({ phase: 'workBills', pendingSignature: null });
        break;
      case 'temperatureCheck':
        set({ phase: 'arrived', enteredTemperature: null });
        break;
      default:
        break;
    }
  },

  setRoomLeft: (n) => set({ roomLeft: n }),
  setSignerName: (name) => set({ signerName: name }),
  setAvs: (patch) => set((s) => ({ avs: { ...s.avs, ...patch } })),
  setBillNoFreight: (id, value) =>
    set((s) => ({ bills: { ...s.bills, [id]: { ...s.bills[id], noFreight: value } } })),
  setBillConfirmed: (id, value) =>
    set((s) => ({ bills: { ...s.bills, [id]: { ...s.bills[id], confirmed: value } } })),
  setBillNotes: (id, notes) =>
    set((s) => ({ bills: { ...s.bills, [id]: { ...s.bills[id], notes } } })),
  addBillPhoto: (id, blob) =>
    set((s) => {
      const current = s.bills[id]?.photos ?? [];
      if (current.length >= MAX_BILL_PHOTOS) return s;
      return { bills: { ...s.bills, [id]: { ...s.bills[id], photos: [...current, blob] } } };
    }),
  removeBillPhoto: (id, index) =>
    set((s) => ({
      bills: {
        ...s.bills,
        [id]: { ...s.bills[id], photos: (s.bills[id]?.photos ?? []).filter((_, i) => i !== index) },
      },
    })),
  setBillBolDoc: (id, blob) =>
    set((s) => ({ bills: { ...s.bills, [id]: { ...s.bills[id], bolDoc: blob } } })),
  captureSignature: (blob) => set({ pendingSignature: blob }),

  complete: async () => {
    const { stop, signerName, roomLeft, avs, bills, pendingSignature } = get();
    if (!stop) return;
    const loc = stopLogLocation(stop);
    const fix = gpsService.peekFix();

    if (roomLeft !== null) {
      // Native ROOM LEFT log: tagged 'ROOM', no assignment record, value lives
      // in the log string itself (Martin parses it).
      await logQueueService.enqueue({
        logType: buildLog.roomLeft(String(roomLeft)),
        display: 'Display',
        tag: 'ROOM',
      });
    }

    for (const b of stop.bills) {
      const bs =
        bills[b.remF04Id] ??
        { noFreight: false, confirmed: false, notes: '', photos: [], bolDoc: null };
      if (bs.noFreight) {
        await logQueueService.enqueue({
          logType: buildLog.deadCallNoFreightPickup(),
          display: 'Display',
          assignmentId: b.remF04Id,
          tag: b.remF02Tag ?? undefined,
          additionalFields: { [CaptureFields.switch999]: '999' },
        });
        await assignmentRepo.markComplete(b.remF04Id);
        continue;
      }

      await logQueueService.enqueue({
        logType: buildLog.finishLoadingAt(loc),
        display: 'Display',
        assignmentId: b.remF04Id,
        tag: b.remF02Tag ?? undefined,
      });
      const departed = await logQueueService.enqueue({
        logType: buildLog.pupDepartedFrom(loc),
        display: 'Display',
        assignmentId: b.remF04Id,
        tag: b.remF02Tag ?? undefined,
        latitude: fix?.latitude,
        longitude: fix?.longitude,
        accuracy: fix?.accuracy,
        additionalFields: {
          [CaptureFields.pickupName]: signerName,
          [CaptureFields.pickupNotes]: bs.notes || null,
          [AvsPickupFields.inside]: yn(avs.inside),
          [AvsPickupFields.tailgate]: yn(avs.tailgate),
          [AvsPickupFields.residential]: yn(avs.residential),
          [AvsPickupFields.dangerousGoods]: yn(avs.dangerousGoods),
        },
      });
      if (pendingSignature) {
        await mediaQueueService.enqueue({
          kind: 'pickup-signature',
          assignmentId: b.remF04Id,
          logClientId: departed.id,
          fieldName: MediaFields.pickupSignature,
          blob: pendingSignature,
          filename: `pickup-sig-${b.remF04Id}-${Date.now()}.png`,
          mimeType: 'image/png',
          tag: b.remF02Tag,
          driverNumber: b.remF01User,
        });
      }
      for (let i = 0; i < bs.photos.length; i++) {
        const blob = bs.photos[i];
        const slot = (i + 1) as 1 | 2 | 3 | 4 | 5;
        await mediaQueueService.enqueue({
          kind: 'pickup-photo',
          assignmentId: b.remF04Id,
          logClientId: departed.id,
          fieldName: MediaFields.pickupPhoto(slot),
          blob,
          filename: `pickup-${b.remF04Id}-${slot}-${Date.now()}.jpg`,
          mimeType: blob.type || 'image/jpeg',
          tag: b.remF02Tag,
          driverNumber: b.remF01User,
        });
      }
      if (bs.bolDoc) {
        await mediaQueueService.enqueue({
          kind: 'bol-doc',
          assignmentId: b.remF04Id,
          logClientId: departed.id,
          fieldName: MediaFields.bolMaster,
          blob: bs.bolDoc,
          filename: `bol-${b.remF04Id}-${Date.now()}.jpg`,
          mimeType: bs.bolDoc.type || 'image/jpeg',
          tag: b.remF02Tag,
          driverNumber: b.remF01User,
        });
      }
      // Native keeps the picked-up bill at switch 2 so it appears in the
      // unload (delivery) list.
      await assignmentRepo.setLocalSwitch(b.remF04Id, DevSwitch.Pickup);
    }

    set({ phase: 'complete' });
  },

  reset: () =>
    set({
      stop: null,
      phase: 'idle',
      requiresProbe: false,
      enteredTemperature: null,
      roomLeft: null,
      signerName: '',
      avs: { ...emptyAvs },
      bills: {},
      pendingSignature: null,
      error: null,
    }),
    }),
    {
      name: 'pickup-workflow',
      // sessionStorage: survives a page refresh / PWA relaunch within the tab,
      // so an in-progress pickup resumes instead of showing a blank screen.
      storage: createJSONStorage(() => sessionStorage),
      // Persist only serializable workflow state -- never Blobs (photos, BOL,
      // signature stay in memory and are re-captured if lost on hard refresh).
      partialize: (s) => ({
        stop: s.stop,
        phase: s.phase,
        requiresProbe: s.requiresProbe,
        enteredTemperature: s.enteredTemperature,
        roomLeft: s.roomLeft,
        signerName: s.signerName,
        avs: s.avs,
        bills: Object.fromEntries(
          Object.entries(s.bills).map(([k, b]) => [
            k,
            {
              noFreight: b.noFreight,
              confirmed: b.confirmed,
              notes: b.notes,
              photos: [],
              bolDoc: null,
            },
          ]),
        ),
      }),
    },
  ),
);

export const pickupSelectors = {
  temperatureExceedsThreshold: (s: PickupState) => temperatureExceeds(s.enteredTemperature),
  // Native gate: every bill handled (confirmed or no-freight) AND at least one
  // real pickup confirmed. If all bills are no-freight, use the stop dead call.
  canContinueToSignature: (s: PickupState) => {
    if (!s.stop) return false;
    const ids = s.stop.bills.map((b) => b.remF04Id);
    const allHandled = ids.every((id) => s.bills[id]?.confirmed || s.bills[id]?.noFreight);
    const someConfirmed = ids.some((id) => s.bills[id]?.confirmed);
    return allHandled && someConfirmed;
  },
};
